// apps/web/src/lib/authFetch.ts
import { supabase } from './supabase';

/**
 * Auth-aware fetch:
 * - Adds Authorization: Bearer <access_token> if present
 * - Adds X-Timezone header (e.g. "Europe/Berlin")
 * - Never forces Content-Type unless there is a non-FormData body
 * - Retries once on 401 after a silent refresh; does NOT auto-logout on failure
 * - Preserves custom headers
 */
export async function authFetch(url: string, init?: RequestInit): Promise<Response> {
  const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

  // Build headers without clobbering caller-provided ones
  const headers = new Headers(init?.headers || {});

  // Only set Content-Type if there is a body and it's not FormData and caller didn't set it
  const hasBody = typeof init?.body !== 'undefined' && init?.body !== null;
  if (hasBody && !(init?.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  // Default Accept (caller can override)
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');

  headers.set('X-Timezone', viewerTz);

  // Attach access token (prefer fast local copy, then supabase)
  let token: string | null = null;
  try {
    token = localStorage.getItem('token');
  } catch {}
  if (!token) {
    const { data } = await supabase.auth.getSession();
    token = data.session?.access_token ?? null;
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);

  // First attempt
  let res = await fetch(url, { ...(init || {}), headers });

  // If unauthorized, try one silent refresh and retry once
  if (res.status === 401) {
    try {
      const { data: refreshed, error } = await supabase.auth.refreshSession();
      const newToken = !error ? refreshed?.session?.access_token : null;
      if (newToken) {
        const retryHeaders = new Headers(headers);
        retryHeaders.set('Authorization', `Bearer ${newToken}`);
        res = await fetch(url, { ...(init || {}), headers: retryHeaders });
      }
    } catch {
      // no-op
    }

    if (res.status === 401) {
      // Don’t auto-logout. Let the caller decide (e.g., open login modal).
      try {
        window.dispatchEvent(new Event('auth:unauthorized'));
      } catch {}
      return res;
    }
  }

  return res; // ← missing before
}

/**
 * Convenience helper: `authFetch` + JSON parse with better errors.
 * Returns `{ ok, status, data, errorText }`
 */
export async function authFetchJSON<T = any>(
  url: string,
  init?: RequestInit
): Promise<{ ok: boolean; status: number; data: T | null; errorText: string | null }> {
  const res = await authFetch(url, init);
  const status = res.status;
  const ok = res.ok;

  // Try to parse JSON safely (avoid parsing empty responses)
  let parsed: any = null;
  try {
    const text = await res.text();
    parsed = text ? JSON.parse(text) : null;
  } catch {
    // Non-JSON or parse error; leave parsed = null
  }

  return {
    ok,
    status,
    data: ok ? (parsed as T) : null,
    errorText: ok ? null : (parsed?.error || parsed?.message || `${status}`),
  };
}
